<script>
  import { onMount } from 'svelte';
  import { format } from 'svelte-i18n';

  import { getNotificationsContext } from '@client/components/notifications';

  import { AssetsService } from '@client/services/assets';

  import { appStore } from '@client/stores/app';

  import { GeneralDeleteForm } from '../index.js';

  /**
   * Controls whether the next button in the workflow step is disabled.
   * @type {boolean}
   * @default true
   */
  export let nextDisabled = true;

  /**
   * Callback function invoked when the workflow step executes.
   * @type {Function | null}
   * @default null
   */
  export let onExecute = null;

  /**
   * Callback function invoked when the workflow step is rolled back.
   * @type {Function | null}
   * @default null
   */
  export let onRollback = null;

  /**
   * Step configuration object containing step data and metadata.
   * @type {Object}
   * @default { data: {} }
   */
  export let step = { data: {} };

  const { addNotification } = getNotificationsContext();

  const notify = (text, type = 'success', autohide = 2500, position = 'top-right') => {
    addNotification({ text, type, autohide, position });
  };

  const getDocuments = async () => {
    const documents = await AssetsService.getAllDocuments();
    return documents.map((document) => ({
      label: document.rulename,
      value: document.rulename,
      ...document
    }));
  };

  const onDeleteDocument = async (document) => {
    const deleteResponse = await AssetsService.deleteDocument(document);

    if (deleteResponse?.status >= 400) {
      notify($format('label.FAILURE.DELETE', { values: { type: document.rulename } }), 'error');
      throw new Error($format('label.FAILURE.DELETE_DOCUMENT'));
    }

    notify($format('label.SUCCESS.DELETE', { values: { type: document.rulename } }));

    step.data = {
      id: document.rulename,
      documentData: {
        rulename: document.rulename,
        hash: document.hash,
        filesize: document.filesize,
        filename: document.filename,
        noncompliant: document.noncompliant,
        viewonly: document.viewonly
      }
    };
  };

  const rollbackDeleteDocument = async () => {
    if (!step.data?.documentData) {
      return;
    }

    $appStore.loading = true;

    try {
      const response = await AssetsService.addDocument(step.data.documentData);

      if (response?.status >= 400) {
        throw new Error($format('label.FAILURE.ROLLBACK_DOCUMENT_DELETION'));
      }
    } catch (error) {
      console.error('Failed to rollback document deletion:', error);
    } finally {
      $appStore.loading = false;
    }
  };
</script>

<GeneralDeleteForm
  bind:nextDisabled
  bind:step
  {onExecute}
  {onRollback}
  rollbackFunction={rollbackDeleteDocument}
  getFunction={getDocuments}
  deleteFunction={onDeleteDocument}
  showPreview={true}
>
  <div slot="preview" let:item>
    {#if item}
      <div class="document-preview">
        <div class="document-details">
          <div class="detail-item">
            <span class="label">{$format('label.NAME')}:</span>
            <span class="value document-name">{item.rulename}</span>
          </div>
          <div class="detail-item">
            <span class="label">{$format('label.FILENAME')}:</span>
            <span class="value">{item.filename}</span>
          </div>
          {#if item.filesize}
            <div class="detail-item">
              <span class="label">{$format('label.SIZE')}:</span>
              <span class="value">{(item.filesize / 1024).toFixed(2)} KB</span>
            </div>
          {/if}
          {#if item.viewonly}
            <div class="detail-item">
              <span class="label">{$format('label.VIEW_ONLY')}:</span>
              <span class="value status-active">{$format('label.YES')}</span>
            </div>
          {/if}
          {#if item.noncompliant}
            <div class="detail-item">
              <span class="label">{$format('label.NON_COMPLIANT')}:</span>
              <span class="value status-warning">{$format('label.YES')}</span>
            </div>
          {/if}
        </div>
      </div>
    {/if}
  </div>
</GeneralDeleteForm>

<style lang="scss">
  .document-preview {
    background-color: var(--color-white);
    padding: var(--padding-base);

    .document-details {
      display: flex;
      flex-direction: column;
      gap: var(--margin-small);
      width: 100%;

      .detail-item {
        display: flex;
        gap: var(--margin-small);

        .label {
          color: var(--color-gray-600);
          flex-shrink: 0;
          font-weight: var(--font-weight-medium);
        }

        .value {
          color: var(--color-gray-900);

          &.document-name {
            font-weight: var(--font-weight-medium);
          }

          &.status-active {
            color: var(--color-green-600);
            font-weight: var(--font-weight-medium);
          }

          &.status-warning {
            color: var(--color-orange-600);
            font-weight: var(--font-weight-medium);
          }
        }
      }
    }
  }
</style>
